import { getCookie, setCookie } from './cookies';

export const CONSENT_COOKIE = 'hessian_cookie_consent';
const CONSENT_MAX_AGE = 60 * 60 * 24 * 365;

export type ConsentState = 'accepted' | 'rejected' | 'unset';

// EU + UK + EEA + Switzerland (ISO 3166-1 alpha-2). Visitors from these
// regions get explicit opt-in; everyone else gets opt-out (banner still
// shows, but the pixel loads while they decide).
const OPT_IN_COUNTRIES = new Set([
	'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
	'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
	'SI', 'ES', 'SE', 'GB', 'IS', 'LI', 'NO', 'CH'
]);

const GEO_SESSION_KEY = 'hessian_cc_geo';

export function readConsent(): ConsentState {
	const raw = getCookie(CONSENT_COOKIE);
	if (raw === 'accepted' || raw === 'rejected') return raw;
	return 'unset';
}

function writeConsent(value: 'accepted' | 'rejected') {
	setCookie(CONSENT_COOKIE, value, {
		maxAge: CONSENT_MAX_AGE,
		sameSite: 'Lax',
		secure: typeof location !== 'undefined' && location.protocol === 'https:'
	});
}

// Best-effort country lookup via Cloudflare's open trace endpoint. Returns
// undefined on failure — callers should treat that as "unknown", which
// (per our policy) means show the banner and assume opt-in to be safe.
export async function lookupCountry(): Promise<string | undefined> {
	if (typeof window === 'undefined') return undefined;
	try {
		const cached = sessionStorage.getItem(GEO_SESSION_KEY);
		if (cached) return cached;
	} catch {
		// sessionStorage unavailable (private mode quirks) — fall through and refetch.
	}

	try {
		const res = await fetch('https://www.cloudflare.com/cdn-cgi/trace', {
			cache: 'no-store'
		});
		if (!res.ok) return undefined;
		const text = await res.text();
		const match = text.match(/^loc=([A-Z]{2})$/m);
		const country = match?.[1];
		if (country) {
			try {
				sessionStorage.setItem(GEO_SESSION_KEY, country);
			} catch {
				// ignore storage failures
			}
		}
		return country;
	} catch {
		return undefined;
	}
}

export function isOptInCountry(country: string | undefined): boolean {
	// Unknown country → treat as opt-in (the safe default).
	if (!country) return true;
	return OPT_IN_COUNTRIES.has(country);
}

// Reactive consent store. Components import this singleton and bind to its
// `state` field for reactivity in Svelte 5 runes.
class CookieConsent {
	state = $state<ConsentState>('unset');
	country = $state<string | undefined>(undefined);
	// UI-only flag so a "Cookie preferences" link can re-show the banner
	// WITHOUT mutating consent. Mutating consent on reopen would silently
	// flip a non-opt-in visitor from rejected→unset, which apollo-pixel reads
	// as "load the script" — re-injecting the tracker without re-consent.
	preferencesOpen = $state(false);

	hydrate() {
		this.state = readConsent();
	}

	async resolveCountry() {
		this.country = await lookupCountry();
	}

	accept() {
		writeConsent('accepted');
		this.state = 'accepted';
		this.preferencesOpen = false;
	}

	reject() {
		writeConsent('rejected');
		this.state = 'rejected';
		this.preferencesOpen = false;
	}

	// Re-show the banner from a "Cookie preferences" link without touching
	// consent state. The banner closes again on the next accept/reject.
	reopen() {
		this.preferencesOpen = true;
	}

	get requiresOptIn(): boolean {
		return isOptInCountry(this.country);
	}
}

export const cookieConsent = new CookieConsent();
